import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from "lucide-react";
import NavBar from "../components/layout/NavBar";
import Meta from "../components/Meta";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";

/* =============== DESIGN TOKENS =============== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    successLight: "#d1fae5",
    error: "#dc2626",
    errorLight: "#fee2e2",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 6px 20px rgba(15, 23, 42, 0.07)",
    lg: "0 12px 40px rgba(15, 23, 42, 0.12)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

/* =============== ANIMATIONS =============== */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const GlobalStyle = createGlobalStyle`
  body { 
    background: ${theme.colors.background}; 
    font-family: ${theme.font}; 
  }
`;

/* =============== LAYOUT =============== */
const Page = styled.div`
  min-height: 100vh;
  padding-top: 64px;
  background: linear-gradient(180deg, #f8fafc, #f9fbff 60%, #f8fafc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 24px 40px;
  
  @media (max-width: 768px) {
    padding: 64px 16px 24px;
  }
`;

const Container = styled.div`
  background: linear-gradient(135deg, ${theme.colors.surface} 0%, #fafbff 100%);
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  width: 480px;
  max-width: 100%;
  padding: 40px 36px;
  box-shadow: ${theme.shadows.lg}, 
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
  animation: ${fadeIn} 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.primary}, #2563eb);
    border-radius: 20px 20px 0 0;
  }
  
  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 16px;
    margin: 0;
    width: 100%;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  .icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, ${theme.colors.primary}, #2563eb);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
    
    svg {
      color: white;
    }
  }
  
  h1 {
    margin: 0 0 8px;
    font-size: 1.75rem;
    font-weight: 800;
    color: ${theme.colors.textHeading};
    letter-spacing: -0.02em;
  }
  
  p {
    margin: 0;
    color: ${theme.colors.textMuted};
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    
    .icon {
      width: 56px;
      height: 56px;
      margin-bottom: 16px;
    }
    
    h1 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormField = styled.div`
  position: relative;
  
  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${theme.colors.textBody};
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 2px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 0 18px 0 48px;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${theme.colors.textBody};
  outline: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.12);
    transform: translateY(-1px);
  }
  
  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px ${theme.colors.primaryLight},
                0 8px 24px rgba(59, 130, 246, 0.15);
    transform: translateY(-1px);
  }
  
  &::placeholder {
    color: ${theme.colors.textMuted};
    font-weight: 400;
  }
  
  &.error {
    border-color: ${theme.colors.error};
    background: linear-gradient(135deg, #fef7f7 0%, #fef2f2 100%);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.primary};
  opacity: 0.7;
  transition: opacity 0.2s ease;
  
  ${Input}:focus + & {
    opacity: 1;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const SubmitButton = styled.button`
  height: 52px;
  background: linear-gradient(
    135deg,
    ${theme.colors.primary} 0%,
    #2563eb 100%
  );
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3), 
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent);
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${fadeIn} 0.3s ease;
  
  &.error {
    background: ${theme.colors.errorLight};
    border: 1px solid #fca5a5;
    color: ${theme.colors.error};
  }
  
  &.success {
    background: ${theme.colors.successLight};
    border: 1px solid #a7f3d0;
    color: ${theme.colors.success};
    animation: ${pulse} 0.5s ease;
  }
`;

const Footer = styled.div`
  margin-top: 24px;
  text-align: center;
  
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${theme.colors.primary};
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      color: #2563eb;
      text-decoration: underline;
    }
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 20px 0;
  
  .success-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, ${theme.colors.success}, #16a34a);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    animation: ${pulse} 1s ease;
    
    svg {
      color: white;
    }
  }
  
  h2 {
    margin: 0 0 12px;
    color: ${theme.colors.textHeading};
    font-size: 1.4rem;
    font-weight: 700;
  }
  
  p {
    margin: 0;
    color: ${theme.colors.textMuted};
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

/* =============== COMPONENT =============== */
const ResetPassword = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const tokenFromUrl = new URLSearchParams(location.search).get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setErrorMsg("Invalid or missing reset token. Please request a new password reset.");
    }
  }, [location.search]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setErrorMsg("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setErrorMsg("Please fill in both password fields.");
      return;
    }

    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    if (!token) {
      setErrorMsg("Invalid reset token. Please request a new password reset.");
      return;
    }

    setIsLoading(true);
    setErrorMsg("");

    try {
      const endpoint = API_CONSTANT_MAP.reset_password;
      const result = await request("POST", endpoint, {
        password,
        token,
      });
      
      if (result.data.success) {
        setIsSuccess(true);
        setTimeout(() => {
          navigate("/signin");
        }, 3000);
      } else {
        setErrorMsg(result.data.error?.message || "Failed to reset password.");
      }
    } catch (error) {
      setErrorMsg(error.error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignIn = () => navigate("/signin");

  return (
    <>
      <GlobalStyle />
      <Meta pageMeta={{ title: "Reset Password - Doitly" }}>
        <NavBar user={props.state?.user} />
        <Page>
          <Container>
            {isSuccess ? (
              <SuccessContainer>
                <div className="success-icon">
                  <CheckCircle size={32} />
                </div>
                <h2>Password Reset Successfully!</h2>
                <p>
                  Your password has been updated. You will be redirected to the sign-in page shortly.
                </p>
              </SuccessContainer>
            ) : (
              <>
                <Header>
                  <div className="icon">
                    <Lock size={24} />
                  </div>
                  <h1>Set New Password</h1>
                  <p>
                    Enter your new password below to complete the reset process
                  </p>
                </Header>

                <Form onSubmit={handleSubmit}>
                  <FormField>
                    <label htmlFor="password">New Password</label>
                    <InputWrapper>
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={handlePasswordChange}
                        placeholder="Enter your new password"
                        className={errorMsg && !password ? "error" : ""}
                      />
                      <InputIcon>
                        <Lock size={18} />
                      </InputIcon>
                      <ToggleButton
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </ToggleButton>
                    </InputWrapper>
                  </FormField>

                  <FormField>
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <InputWrapper>
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        placeholder="Confirm your new password"
                        className={errorMsg && !confirmPassword ? "error" : ""}
                      />
                      <InputIcon>
                        <Lock size={18} />
                      </InputIcon>
                      <ToggleButton
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </ToggleButton>
                    </InputWrapper>
                  </FormField>

                  {errorMsg && (
                    <Message className="error">
                      {errorMsg}
                    </Message>
                  )}

                  <SubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? "Saving..." : "Save New Password"}
                  </SubmitButton>
                </Form>
              </>
            )}

            <Footer>
              <div 
                className="back-link" 
                onClick={handleBackToSignIn}
              >
                <ArrowLeft size={16} />
                Back to Sign In
              </div>
            </Footer>
          </Container>
        </Page>
      </Meta>
    </>
  );
};

const mapStateToProps = (state) => ({ state });

export default connect(mapStateToProps)(ResetPassword);
